import {
  LockOutlined,
  LoginOutlined,
  ThunderboltFilled,
  UserOutlined,
} from "@ant-design/icons";
import { Alert, Button, Card, Form, Input, Typography } from "antd";
import { useTranslation } from "react-i18next";

import type { AdminPublic } from "@/shared/api";

import { errorMessage, useLogin } from "../model/useLogin";

interface Values {
  login: string;
  password: string;
}

/**
 * Форма входа.
 *
 * Одно сообщение об ошибке на всю форму, а не подсветка конкретного поля:
 * бэкенд намеренно отвечает одинаково на неверный логин, неверный пароль и
 * запертую лимитом попыток учётку, и расшивать эту одинаковость обратно на
 * клиенте — значит вернуть проверялку существующих логинов, от которой на
 * сервере ушли.
 *
 * Отсюда же следует, что подсказки «учётка заблокирована» здесь быть не может:
 * причину видит только суперадмин на странице «Администраторы».
 */
export function LoginForm({ onSuccess }: { onSuccess: (admin: AdminPublic) => void }) {
  const { t } = useTranslation();
  const mutation = useLogin(onSuccess);

  return (
    <Card style={{ width: 380, maxWidth: "100%" }}>
      <div style={{ textAlign: "center", marginBottom: 20 }}>
        <span
          aria-hidden
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 46,
            height: 46,
            borderRadius: 15,
            background: "linear-gradient(135deg, #1677ff 0%, #06b6d4 100%)",
            color: "#fff",
            fontSize: 22,
            marginBottom: 12,
          }}
        >
          <ThunderboltFilled aria-hidden />
        </span>
        <Typography.Title level={3} style={{ margin: 0 }}>
          {t("login.title")}
        </Typography.Title>
        <Typography.Text type="secondary">{t("login.hint")}</Typography.Text>
      </div>

      {mutation.isError && (
        <Alert
          type="error"
          showIcon
          title={errorMessage(mutation.error)}
          style={{ marginBottom: 16 }}
        />
      )}

      <Form<Values>
        layout="vertical"
        requiredMark={false}
        onFinish={(values) => {
          mutation.mutate(values);
        }}
      >
        <Form.Item
          name="login"
          label={t("login.login")}
          rules={[{ required: true, message: t("login.required") }]}
        >
          <Input autoFocus autoComplete="username" prefix={<UserOutlined aria-hidden />} />
        </Form.Item>

        <Form.Item
          name="password"
          label={t("login.password")}
          rules={[{ required: true, message: t("login.required") }]}
        >
          <Input.Password autoComplete="current-password" prefix={<LockOutlined aria-hidden />} />
        </Form.Item>

        <Button
          type="primary"
          htmlType="submit"
          block
          size="large"
          icon={<LoginOutlined aria-hidden />}
          loading={mutation.isPending}
        >
          {t("login.submit")}
        </Button>
      </Form>
    </Card>
  );
}
